import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { 
  FiLayers, FiUsers, FiActivity, FiAlertCircle, 
  FiCheckCircle, FiHardDrive, FiRefreshCw, FiClock,
  FiPlus, FiFilter, FiChevronDown, FiHelpCircle,
  FiTarget, FiCpu, FiShield, FiZap
} from 'react-icons/fi';

const STEPS = [
  { id: 1, title: 'BIM Digitization', desc: 'Sync 3D blueprints and structural metadata into the project core.', icon: FiLayers },
  { id: 2, title: 'Team Mobilization', desc: 'Deploy subcontractors to specific site nodes with automated onboarding.', icon: FiUsers },
  { id: 3, title: 'IoT Telemetry', desc: 'Connect site sensors and wearables for real-time safety tracking.', icon: FiActivity },
  { id: 4, title: 'AI Conflict Detection', desc: 'Identify blueprint mismatches and route RFIs to engineers instantly.', icon: FiZap },
  { id: 5, title: 'Compliance Audit', desc: 'Execute safety checklists with tamper-proof blockchain logs.', icon: FiShield },
  { id: 6, title: 'Stakeholder Sync', desc: 'Generate progress reports for 100% investor transparency.', icon: FiHardDrive },
  { id: 7, title: 'Project Handover', desc: 'Transition site data to facility management for operational launch.', icon: FiRefreshCw },
];

const FAQS = [
  { q: "How long does the initial BIM sync take?", a: "For a standard 20-story commercial core, the initial digitization and node mapping takes approximately 4-6 hours." },
  { q: "Is the dummy data representative of real site metrics?", a: "Yes. Our simulated environment uses historical data from over 500 completed high-rise projects to ensure realistic telemetry." },
  { q: "How does BuildBeam handle offline site conditions?", a: "Site nodes cache data locally and sync automatically via Starlink or LTE mesh once connectivity is restored." },
  { q: "Can subcontractors access the platform for free?", a: "BuildBeam operates on a 'Seat-Free' model for subs; GCs pay per project, allowing unlimited crew access." },
  { q: "Is our project data private and secure?", a: "Absolutely. We use AES-256 encryption and private cloud clusters for every enterprise-tier contractor." }
];

export const DemoPage = () => {
  const [activeState, setActiveState] = useState('success');
  const [openFaq, setOpenFaq] = useState(null);

  const states = {
    before: { label: 'Before', desc: 'Awaiting site mobilization.', color: 'slate', icon: FiClock },
    progress: { label: 'In Progress', desc: 'Active sensor telemetry.', color: 'cyan', icon: FiRefreshCw, animate: true },
    success: { label: 'Success', desc: 'Milestone verified.', color: 'green', icon: FiCheckCircle },
    warning: { label: 'Warning', desc: 'Conflict detected in Sector B.', color: 'yellow', icon: FiAlertCircle },
    error: { label: 'Error', desc: 'Node hardware offline.', color: 'red', icon: FiAlertCircle }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto space-y-12 pb-24">
      
      {/* 1. Overview / What it does */}
      <section className="relative overflow-hidden p-8 rounded-sm border border-slate-800 bg-slate-900/40">
        <div className="absolute top-0 right-0 p-10 opacity-10 pointer-events-none">
          <SafeIcon icon={FiTarget} className="text-9xl text-cyan-500" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <Badge variant="cyan" className="mb-4">System Overview</Badge>
          <h1 className="text-4xl font-bold text-slate-100 mb-4 tracking-tight">
            BuildBeam: The Site Intelligence Operating System
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed font-light">
            BuildBeam is a high-fidelity control room for General Contractors. We solve the problem of 
            <span className="text-cyan-400"> fragmented site telemetry</span> by unifying BIM models, 
            IoT sensor feeds, and subcontractor workflows into a single structural source of truth.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="flex gap-3">
              <div className="mt-1 text-yellow-500"><SafeIcon icon={FiCpu} /></div>
              <div>
                <h4 className="text-sm font-bold text-slate-200">The Problem</h4>
                <p className="text-xs text-slate-500">Site delays often stem from missed RFI signals and siloed manpower data.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-1 text-emerald-500"><SafeIcon icon={FiCheckCircle} /></div>
              <div>
                <h4 className="text-sm font-bold text-slate-200">The Outcome</h4>
                <p className="text-xs text-slate-500">Zero-latency coordination and 18% reduction in structural rework costs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works + Try this flow */}
      <section className="space-y-6">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-slate-100 uppercase tracking-tighter">Demo / How It Works</h2>
          <div className="h-px flex-1 bg-slate-800"></div>
          <span className="text-[10px] font-mono text-slate-600">SOP-001</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-4 p-4 bg-yellow-950/10 border border-yellow-900/20 rounded-sm">
             <h3 className="text-xs font-bold text-yellow-500 uppercase mb-2 flex items-center gap-2">
              <SafeIcon icon={FiActivity} /> Try this flow
             </h3>
             <p className="text-xs text-slate-400">Follow the path from digital twin synchronization to final asset handover.</p>
          </div>
          {STEPS.map((step) => (
            <Card key={step.id} className="border-slate-800/60 hover:border-cyan-500/40 transition-all group">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-sm bg-slate-800 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500 group-hover:text-slate-950 transition-colors shrink-0">
                  <SafeIcon icon={step.icon} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-200 mb-1 leading-none">{step.title}</h4>
                  <p className="text-[11px] text-slate-500 leading-normal">{step.desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* 4. Simulated States (Interactive) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <section className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-100 uppercase tracking-tighter">Simulated States</h2>
            <div className="flex p-1 bg-slate-900 border border-slate-800 rounded-sm gap-1">
              {Object.keys(states).map((s) => (
                <button 
                  key={s}
                  onClick={() => setActiveState(s)}
                  className={`px-3 py-1 text-[10px] uppercase font-mono transition-all rounded-[1px] ${activeState === s ? 'bg-cyan-600 text-slate-950 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <Card className="p-8 border-cyan-900/30 bg-cyan-950/5 relative overflow-hidden">
            <div className={`p-6 border border-${states[activeState].color}-500/30 bg-slate-900/80 rounded-sm flex flex-col md:flex-row items-center gap-8 transition-all duration-500`}>
              <div className={`w-24 h-24 rounded-full border-4 border-${states[activeState].color}-500/20 flex items-center justify-center text-4xl text-${states[activeState].color}-400 shadow-[0_0_30px_-10px] shadow-${states[activeState].color}-500/30`}>
                <SafeIcon icon={states[activeState].icon} className={states[activeState].animate ? 'animate-spin-slow' : ''} />
              </div>
              <div className="flex-1 text-center md:text-left">
                <Badge variant={states[activeState].color} className="mb-2">{states[activeState].label} State</Badge>
                <h3 className="text-2xl font-bold text-slate-100 mb-2">Sector Node BB-X901</h3>
                <p className="text-slate-400 font-mono text-sm">{states[activeState].desc}</p>
                <div className="mt-6 flex gap-4 justify-center md:justify-start">
                  <div className="text-center">
                    <p className="text-[10px] text-slate-600 uppercase font-mono">Sync Rate</p>
                    <p className="text-sm font-bold text-slate-300">1.4 GB/s</p>
                  </div>
                  <div className="w-px h-8 bg-slate-800"></div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-600 uppercase font-mono">Uptime</p>
                    <p className="text-sm font-bold text-slate-300">99.98%</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* 5. FAQ */}
        <section className="space-y-6">
          <h2 className="text-xl font-bold text-slate-100 uppercase tracking-tighter">FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((faq, idx) => (
              <div key={idx} className="border border-slate-800 rounded-sm overflow-hidden">
                <button 
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between bg-slate-900/50 hover:bg-slate-800/50 transition-colors text-left"
                >
                  <span className="text-xs font-bold text-slate-300 pr-4">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === idx && (
                    <motion.div 
                      initial={{ height: 0 }} 
                      animate={{ height: 'auto' }} 
                      exit={{ height: 0 }} 
                      className="overflow-hidden bg-slate-950/50"
                    >
                      <div className="p-4 text-[11px] text-slate-500 leading-relaxed border-t border-slate-800">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
          <div className="p-4 bg-slate-900/30 border border-dashed border-slate-800 rounded-sm text-center">
            <p className="text-[10px] text-slate-600 uppercase mb-2">Need direct support?</p>
            <button className="text-xs font-bold text-cyan-500 hover:text-cyan-400">Open Command Channel</button>
          </div>
        </section>
      </div>

      {/* 3. Sample Data Section */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-100 uppercase tracking-tighter">Sample Data</h2>
          <Badge variant="default">4 Live Records Loaded</Badge>
        </div>
        <Card className="p-0 border-slate-800/40">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px] font-mono">
              <thead className="bg-slate-900/80 border-b border-slate-800 text-slate-500 uppercase">
                <tr>
                  <th className="px-6 py-4">Ref ID</th>
                  <th className="px-6 py-4">Module</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Last Entry</th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/30">
                <tr className="hover:bg-cyan-500/5 transition-colors group">
                  <td className="px-6 py-4 text-cyan-500">RFI-089</td>
                  <td className="px-6 py-4 text-slate-300 font-sans font-bold">RFI Tracker</td>
                  <td className="px-6 py-4"><Badge variant="yellow">Open</Badge></td>
                  <td className="px-6 py-4 text-slate-500">HVAC routing conflict Floor 4. Escalated to Mech-Eng.</td>
                  <td className="px-6 py-4 text-right"><button className="text-slate-600 group-hover:text-cyan-400">Sync</button></td>
                </tr>
                <tr className="hover:bg-cyan-500/5 transition-colors group">
                  <td className="px-6 py-4 text-cyan-500">DEL-101</td>
                  <td className="px-6 py-4 text-slate-300 font-sans font-bold">Material Logic</td>
                  <td className="px-6 py-4"><Badge variant="cyan">En Route</Badge></td>
                  <td className="px-6 py-4 text-slate-500">Steel beams ETA 14:00. GPS tracking active.</td>
                  <td className="px-6 py-4 text-right"><button className="text-slate-600 group-hover:text-cyan-400">Sync</button></td>
                </tr>
                <tr className="hover:bg-cyan-500/5 transition-colors group">
                  <td className="px-6 py-4 text-cyan-500">SAF-902</td>
                  <td className="px-6 py-4 text-slate-300 font-sans font-bold">Safety Cmd</td>
                  <td className="px-6 py-4"><Badge variant="red">Failed</Badge></td>
                  <td className="px-6 py-4 text-slate-500">Leak detected in Sector B hazardous storage.</td>
                  <td className="px-6 py-4 text-right"><button className="text-slate-600 group-hover:text-cyan-400">Sync</button></td>
                </tr>
                <tr className="hover:bg-cyan-500/5 transition-colors group">
                  <td className="px-6 py-4 text-cyan-500">SUB-03</td>
                  <td className="px-6 py-4 text-slate-300 font-sans font-bold">Sub Board</td>
                  <td className="px-6 py-4"><Badge variant="green">On-site</Badge></td>
                  <td className="px-6 py-4 text-slate-500">Hydra Plumbing: 12 workers checked in at Gate 4.</td>
                  <td className="px-6 py-4 text-right"><button className="text-slate-600 group-hover:text-cyan-400">Sync</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </section>

    </motion.div>
  );
};